/**
 * 사전 엔티티(검색바 추천으로 고르는 브랜드·성분·피부고민·제품군)의 **값** — 갈래 목록 · 이름표 · 표기 상한.
 *
 * 🔑 상한은 갈래마다 다르다 — 표기 분포가 다르기 때문이다(항목당 p95: 브랜드 6 · 성분 14 · 피부고민 237).
 *   브랜드·성분은 p95 를 덮고, 피부고민은 「표기까지 넓히기」를 켰을 때만 앞 10개, 제품군은 표기 없이 태그로만 찾는다.
 * ⚠️ 서버 리더도 이 상한으로 거른다 — 화면이 더 보내도 잘린다.
 */

import type { DictionaryEntityType } from 'src/types/dictionary/dictionary-ops';

/** 갈래 — 추천 목록에 이 차례로 묶인다 */
export const DICTIONARY_ENTITY_TYPES: readonly DictionaryEntityType[] = [
  'brand',
  'ingredient',
  'concern',
  'productCategory',
];

/** 칩·추천 목록의 갈래 이름 */
export const DICTIONARY_ENTITY_LABEL: Record<DictionaryEntityType, string> = {
  brand: '브랜드',
  ingredient: '성분',
  concern: '피부고민',
  productCategory: '제품군',
};

/**
 * 갈래별 표기 상한 — 정규명 포함 개수.
 * ⚠️ 피부고민의 10 은 «넓히기를 켰을 때»의 값이다. 끄면 0 이다.
 * 🔑 브랜드는 대표 한글 표기(「아누아」)가 해시태그·계정형 표기 뒤에 적힌 경우가 많아 넉넉히 둔다 —
 *   질의가 무거워지는 것은 절 예산(`server/moments/clauses/entity-clause` `ENTITY_CLAUSE_BUDGET`)이 막는다.
 */
export const DICTIONARY_ENTITY_TERM_CAP: Record<DictionaryEntityType, number> = {
  brand: 100,
  ingredient: 15,
  concern: 10,
  productCategory: 0,
};

/** 정규명·표기 한 개의 최대 길이 — 넘으면 리더가 버린다 */
export const DICTIONARY_ENTITY_TERM_MAX_LENGTH = 60;

/**
 * 추천에서 **표기로도 걸리는** 갈래 — 칩이 「태그 ∪ 표기」로 찾는 갈래만이다.
 * ⚠️ 피부고민(기본)·제품군 칩은 태그로만 찾는다 — 표기로 걸어 주면 친 말(「어성초에센스패드」)로 찾는다고 오해한다.
 */
export const DICTIONARY_ENTITY_SUGGEST_BY_RAW: readonly DictionaryEntityType[] = ['brand', 'ingredient'];

/** 검색바 아래 추천에 보이는 최대 개수 — 전체 */
export const ENTITY_SUGGEST_MAX_ITEMS = 8;

/** 검색바 아래 추천에 보이는 최대 개수 — 갈래마다 */
export const ENTITY_SUGGEST_MAX_PER_TYPE = 3;

/**
 * Research 갈래마다 **정규명 필드가 있는 갈래** — 그 화면의 추천·링크·서버 리더가 이 목록만 받는다.
 *
 * ```
 *   광고   브랜드 canonicalBrand(s) · 피부고민 adConcerns · 제품군 canonicalProductCategory
 *   상품   브랜드 canonicalBrand · 제품군 canonicalProductCategory
 *   이벤트 브랜드 — 상류의 `brand`(정규명 하나)
 * ```
 *
 * ⛔ 정규명 필드가 없는 갈래를 넣지 마라 — 골랐는데 0건이 되는 가짜 칩이 된다.
 *   상품의 `concerns`·`ingredients` 는 원문 태그라 사전 정규명과 안 맞는다(상위 300개 중 피부고민 14개).
 */
export const AD_ENTITY_TYPES: readonly DictionaryEntityType[] = ['brand', 'concern', 'productCategory'];
export const PRODUCT_ENTITY_TYPES: readonly DictionaryEntityType[] = ['brand', 'productCategory'];
export const EVENT_ENTITY_TYPES: readonly DictionaryEntityType[] = ['brand'];

/** Research 광고·상품 검색칸에 넣을 수 있는 칩 수 — 소셜 검색바의 상한과 같다 */
export const RESEARCH_ENTITY_MAX = 3;
/** 이벤트 검색칸은 칩 하나다 — 상류의 `brand` 가 하나만 받는다 */
export const EVENT_ENTITY_MAX = 1;
